import { ApplicableRulesCard, AssetListCard, CampaignListCard, DeliverablesCard, TextListCard } from '@atlas/ui-domain'
import { Tab, TabList, TabPanel, Tabs } from '@atlas/ui-primitives'
import { useNavigate } from 'react-router-dom'
import { toCampaignListItems } from '@/adapters/campaign-list-item'
import type { ProductPage as ProductPageData } from '@/domain/explore.types'
import { useRuleEditing } from '@/hooks/useRuleEditing'
import { downloadAssets } from '@/lib/download-asset'
import { toFileTypeRoles } from '@/lib/file-type-roles'
import { CatalogAdminTabs, useCatalogAdminTabs } from './CatalogAdminTabs'
import { toDeliverableItems } from './deliverables-adapter'
import { ProductEditTab } from './ProductEditTab'
import { ProductMetaCard } from './ProductMetaCard'
import { ProductNamingCard } from './ProductNamingCard'
import { BRAND_KIT_WORDS } from './product-page-words'
import { toExploreRuleListItems } from './rule-list-adapter'
import { toTextListItems } from './text-list-adapter'
import type { useProductSections } from './useProductSections'

/**
 * The product page's right column, in two tabs.
 *
 * Content and record are different questions. The first tab answers what the product IS
 * and what has been made for it, narrowed by the rail and the scope; the second answers
 * what ATLAS has RECORDED about it — its identifiers, its Veeva object, its raw meta —
 * which no filter applies to. Interleaving them, as the single scrolling column did, put a
 * debugging surface at the bottom of a reading surface.
 */

const CONTENT_TAB = 'product'
const EDIT_TAB = 'edit'
const META_TAB = 'meta'

export interface ProductMainTabsProps {
	page: ProductPageData
	scopeLabel: string | null
	isFiltered: boolean
	locale: string | null
	onLocaleChange: (locale: string | null) => void
	channel: string
	onChannelChange: (channel: string) => void
	rulesHref: string
	/**
	 * The section reads, already resolved. Typed FROM the hook rather than restated: these
	 * are seven query results whose shapes are the hook's business, and a hand-written
	 * mirror of them would drift the first time one changed.
	 */
	sections: ReturnType<typeof useProductSections>
	brandKitEmptyLabel: string
	textsEmptyLabel: string
	campaignsEmptyLabel: string
}

export function ProductMainTabs({
	page,
	scopeLabel,
	locale,
	onLocaleChange,
	channel,
	onChannelChange,
	rulesHref,
	sections,
	brandKitEmptyLabel,
	textsEmptyLabel,
	campaignsEmptyLabel,
}: ProductMainTabsProps) {
	const navigate = useNavigate()
	const { applicableTexts, assets, brandKit, campaigns, rules, texts, thumbnails } = sections
	const tabs = useCatalogAdminTabs(CONTENT_TAB, [EDIT_TAB, META_TAB], 'product')
	const { canEdit, editRule, ruleHref, openRule } = useRuleEditing()

	return (
		<Tabs selectedKey={tabs.selectedKey} onSelectionChange={tabs.onSelectionChange}>
			<TabList variant="underline" className="mb-4 flex w-full" aria-label="Product sections">
				<Tab id={CONTENT_TAB}>Product</Tab>
				<CatalogAdminTabs editTabId={EDIT_TAB} metaTabId={META_TAB} canEdit={tabs.canEdit} subject="product" />
			</TabList>

			<TabPanel id={CONTENT_TAB} className="space-y-4">
				{/* Identity before content, and narrowed by neither the rail nor the scope. */}
				<ProductNamingCard names={page.names} />
				<AssetListCard
					{...BRAND_KIT_WORDS}
					roles={toFileTypeRoles(brandKit.files, brandKit.thumbnails)}
					onOpenAsset={(assetId) => void navigate(`/assets/${assetId}`)}
					onDownloadAssets={downloadAssets}
					scopeLabel={scopeLabel}
					emptyLabel={brandKitEmptyLabel}
					isLoading={brandKit.isLoading}
					isError={brandKit.isError}
				/>
				<TextListCard
					texts={toTextListItems(applicableTexts)}
					locale={locale}
					onLocaleChange={onLocaleChange}
					isLoading={texts.isLoading}
					isError={texts.isError}
					emptyLabel={textsEmptyLabel}
				/>
				{/* Above the deliverables: campaigns are the context those are produced for, so
				    they read before the inventory rather than after it. */}
				<CampaignListCard
					campaigns={toCampaignListItems(campaigns.campaigns)}
					viewAllHref="/campaigns"
					onViewAll={() => void navigate('/campaigns')}
					onOpenCampaign={(campaignId) => void navigate(`/campaigns/${campaignId}`)}
					isLoading={campaigns.isLoading}
					isError={campaigns.isError}
					emptyLabel={campaignsEmptyLabel}
				/>
				<DeliverablesCard
					deliverables={toDeliverableItems(assets.data ?? [], thumbnails)}
					channel={channel}
					onChannelChange={onChannelChange}
					onOpenDeliverable={(assetId) => void navigate(`/assets/${assetId}`)}
					emptyLabel={`No delivered assets for ${scopeLabel ?? 'this product'}.`}
					isLoading={assets.isLoading}
					isError={assets.isError}
				/>
				{/* Under the deliverables it covers: the rules are read for the same tuple, so
				    they answer for what the grid above them just listed. */}
				<ApplicableRulesCard
					rules={toExploreRuleListItems(rules.data ?? [])}
					viewAllHref={rulesHref}
					onViewAll={() => void navigate(rulesHref)}
					isLoading={rules.isLoading}
					isError={rules.isError}
					canEdit={canEdit}
					onEditRule={editRule}
					ruleHref={ruleHref}
					onOpenRule={openRule}
					note={canEdit ? 'Editing a rule changes it everywhere it resolves, not only on this product.' : undefined}
				/>
			</TabPanel>

			<TabPanel id={EDIT_TAB}>
				<ProductEditTab page={page} />
			</TabPanel>

			<TabPanel id={META_TAB}>
				<ProductMetaCard page={page} />
			</TabPanel>
		</Tabs>
	)
}
